/// «Вхід» — every word of the section (specs/access.md), in its own file so
/// that it merges cleanly next to other sections (uk.ts carries one line for
/// it). Sentence case; plurals through `plural`.

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const people = (n: number) => `${n} ${plural(n, 'людина', 'людини', 'людей')}`
const keys = (n: number) => `${n} ${plural(n, 'ключ', 'ключі', 'ключів')}`
const visitsOf = (n: number) => `${n} ${plural(n, 'візит', 'візити', 'візитів')}`
const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)
const days = (n: number) => `${n} ${plural(n, 'день', 'дні', 'днів')}`

export const accessUk = {
  nav: 'Вхід',
  title: 'Вхід',
  tabs: {
    desk: 'Рецепція',
    inside: 'Зараз у залі',
    lockers: 'Шафки',
    stats: 'Статистика',
    log: 'Журнал',
    settings: 'Налаштування',
  },
  branch: 'Клуб',
  noBranches: 'Ще немає жодного відкритого клубу',
  noBranchesMessage: 'Вхід працює для клубу: відкрийте його в розділі «Клуби».',

  desk: {
    subtitle: 'Скануйте картку чи QR із застосунку або знайдіть клієнта.',
    scanLabel: 'Картка, QR або клієнт',
    scanPlaceholder: 'Картка, QR, імʼя або телефон',
    scanHint: 'Зчитувач карток і камера вводять сюди самі — поле завжди готове.',
    camera: 'Камера',
    cameraTitle: 'Сканування QR',
    cameraMessage: 'Наведіть камеру на QR у застосунку клієнта.',
    cameraStarting: 'Вмикаємо камеру…',
    cameraDenied: 'Немає доступу до камери. Дозвольте її в налаштуваннях браузера.',
    cameraMissing: 'Камеру не знайдено.',
    cameraUnsupported: 'Цей браузер не вміє читати QR з камери.',
    clear: 'Очистити',
    searching: 'Шукаємо…',
    found: 'Знайдені клієнти',
    none: 'Нікого не знайшли',
    cardNotFound: 'Картку не знайдено. Схожі клієнти:',
    idleTitle: 'Чекаємо на клієнта',
    idleMessage: 'Тут зʼявиться, кого пускаємо і за яким абонементом.',
    checking: 'Перевіряємо…',
    insideSince: (time: string) => `Уже в залі з ${time}`,
    passes: 'Абонементи',
    noPasses: 'У клієнта немає абонементів.',
    willSpend: 'Спишемо цей',
    endsOn: (date: string) => `до ${date}`,
    visitsLeft: (n: number) => `лишилось ${n}`,
    unlimited: 'без ліміту візитів',
    key: 'Ключ',
    keyHint: 'Найменший вільний номерок. Можна вписати інший.',
    keyHintRented: 'Шафка клієнта в оренді — ключ від неї.',
    rentedLocker: (number: string, until: string) => `Орендована шафка №${number} · до ${until}`,
    noFreeKey: 'Вільних ключів немає — пустіть без ключа або поверніть ключ на дошку.',
    admit: 'Пустити',
    admitting: 'Пускаємо…',
    admitAnyway: 'Пустити все одно',
    admitAnywayHint: 'Попередній візит закриється зараз.',
    cancel: 'Скасувати',
    allowedTitle: 'Можна заходити',
    allowed: (name: string) => name,
    allowedKey: (name: string, key: string) => `${name}, ключ ${key}`,
    keyNotIssued: 'Ключ не видано — видайте його в «Зараз у залі».',
    refusedTitle: 'Не пускаємо',
    appNotLinked: 'QR із застосунку, який ще не привʼязано до клієнта клубу.',
    link: 'Привʼязати до клієнта',
    linkTitle: 'Привʼязати застосунок',
    linkMessage:
      'Цей QR — з акаунта eatlete, який ще не привʼязаний до клієнта клубу. Оберіть клієнта — далі він заходитиме за своїм QR.',
    linkClient: 'Клієнт',
    linkSubmit: 'Привʼязати',
    linking: 'Привʼязуємо…',
    linked: (name: string) => `Застосунок привʼязано до клієнта ${name}. Перевірте абонементи й пустіть.`,
    dismiss: 'Готово',
  },

  inside: {
    subtitle: 'Хто зараз у клубі. Оновлюється кожні 15 секунд.',
    now: 'У залі зараз',
    keysOut: 'Ключів видано',
    empty: 'У залі нікого',
    emptyMessage: 'Щойно хтось увійде, він зʼявиться тут.',
    columns: { client: 'Клієнт', since: 'У залі', method: 'Як увійшов', key: 'Ключ', actions: '' },
    sinceLine: (time: string, duration: string) => `з ${time} · ${duration}`,
    noKey: '—',
    left: 'Вийшов',
    issueKey: 'Видати ключ',
    keyReturned: 'Ключ повернуто',
    keyLost: 'Ключ загублено',
    issueField: 'Номер ключа',
    issueSubmit: 'Видати',
    lostTitle: (key: string) => `Ключ ${key} загублено?`,
    lostMessage: 'Ключ зникне з дошки вільних. Плату за нього, якщо вона є, бере каса.',
    lostConfirm: 'Позначити загубленим',
    notReturned: 'Ключі не повернуто',
    notReturnedFootnote: 'Візит уже закінчився — турнікетом або сам, — а ключ ще не повернули.',
    returned: 'Повернуто',
    lost: 'Загублено',
    leftBanner: (name: string) => `${name} вийшов.`,
    columnsKeys: { key: 'Ключ', client: 'Клієнт', actions: '' },

    // «Усі клуби» (specs/access.md §7.2).
    subtitleEvery: 'Хто зараз у ваших клубах. Оновлюється кожні 15 секунд.',
    everyNow: 'У клубах зараз',
    emptyEvery: 'У клубах нікого',
    emptyEveryMessage: 'Щойно хтось увійде в будь-який із ваших клубів, він зʼявиться тут.',
    columnsEvery: { client: 'Клієнт', club: 'Клуб', since: 'У залі', method: 'Як увійшов', key: 'Ключ' },
    clubKeys: (n: number) => `ключів видано ${n}`,
    openClub: (club: string, n: number) => `${club}: ${people(n)}. Відкрити клуб`,
    everyFootnote: '«Вийшов» і ключі — у кожному клубі окремо: оберіть клуб угорі або натисніть на його плитку.',
  },

  /// The header's club picker: everyone inside, every club you work in.
  everyClub: 'Усі клуби',

  /// «Відвідування» on a client's card (specs/access.md §7.1).
  visits: {
    title: 'Відвідування',
    visitsIn: (month: string) => `Візити за ${month}`,
    time: 'Час у залі',
    average: 'Середній візит',
    before: (monthIn: string, value: string) => `${monthIn} ${value}`,
    none: '—',
    today: 'Сьогодні',
    yesterday: 'Учора',
    span: (from: string, to: string) => `${from}–${to}`,
    since: (time: string) => `з ${time}`,
    inside: 'у залі',
    noExit: 'вихід не відмічено',
    key: (number: string) => `ключ ${number}`,
    keyNotReturned: (number: string) => `ключ ${number} не повернуто`,
    empty: 'Відвідувань ще не було.',
    onlyYours: (clubs: number) => (clubs === 1 ? 'Лише ваш клуб' : 'Лише ваші клуби'),
    failed: 'Не вдалося завантажити відвідування',
    unrecorded: (monthIn: string, n: number) =>
      `${sentence(monthIn)} ${visitsOf(n)} ${plural(n, 'закрився сам', 'закрились самі', 'закрились самі')} — без відмітки виходу ${plural(n, 'його', 'їхній', 'їхній')} час не рахуємо.`,
    noneTimed: (monthIn: string) => `${sentence(monthIn)} вихід жодного разу не відмічали — часу в залі ми не знаємо.`,
    insideNow: (time: string) => `Зараз у клубі з ${time}`,
    insideNowAt: (time: string, club: string) => `Зараз у клубі з ${time} · ${club}`,
    months: [
      'січень',
      'лютий',
      'березень',
      'квітень',
      'травень',
      'червень',
      'липень',
      'серпень',
      'вересень',
      'жовтень',
      'листопад',
      'грудень',
    ] as readonly string[],
    monthsIn: [
      'у січні',
      'у лютому',
      'у березні',
      'у квітні',
      'у травні',
      'у червні',
      'у липні',
      'у серпні',
      'у вересні',
      'у жовтні',
      'у листопаді',
      'у грудні',
    ] as readonly string[],
  },

  stats: {
    subtitle: 'Скільки людей приходить, коли пік і скільки тривають тренування.',
    presets: { today: 'Сьогодні', week: '7 днів', month: '30 днів' },
    range: 'Період',
    visits: 'Візити',
    unique: 'Унікальні клієнти',
    peak: 'Пік',
    peakAt: (count: number, when: string) => `${people(count)} · ${when}`,
    peakNone: 'Нікого не було',
    stay: 'Середній час у залі',
    stayMedian: (text: string) => `медіана ${text}`,
    stayNone: 'Ще не рахували',
    stayFootnote: 'Без візитів, що закрились самі.',
    hourly: 'Людей у залі за годинами',
    hourlySub: 'У середньому за день періоду, за часом клубу',
    hourlyLabel: (hour: number, value: string) => `${String(hour).padStart(2, '0')}:00 — ${value} у середньому`,
    hourlySummary: (peak: string) => `Найбільше людей о ${peak}.`,
    hourlyEmpty: 'Немає даних за цей період.',
    daily: 'Візити по днях',
    dailyLabel: (date: string, n: number) => `${date}: ${n}`,
    byMethod: 'Як заходять',
    refusals: 'Відмови',
    refusalsNone: 'Відмов не було.',
    keysNotReturned: (n: number) => `Не повернуто ${keys(n)} за період`,
    table: 'Таблиця',
    tableHour: 'Година',
    tableValue: 'Людей у середньому',
    tableDay: 'День',
    tableVisits: 'Візити',
  },

  log: {
    subtitle: 'Кожна спроба увійти — і чому не пустили. Зберігається 13 місяців.',
    filter: 'Показати',
    all: 'Усі',
    refused: 'Відмови',
    columns: { at: 'Коли', client: 'Клієнт', method: 'Як', outcome: 'Результат', reason: 'Причина' },
    allowed: 'Пустили',
    refusedOne: 'Відмова',
    exit: 'Вихід',
    unknownClient: 'Невідомий',
    empty: 'Ще не було жодної спроби',
    emptyRefused: 'Відмов не було',
    shown: (n: number) => `Останні ${n}`,
  },

  settings: {
    subtitle: 'Як у клубі зі шафками і коли забутий візит закривається сам.',
    lockers: 'Шафки',
    mode: 'Шафки в клубі',
    modes: { keys: 'З номерками', self: 'Без номерків', none: 'Немає' },
    modeHints: {
      keys: 'Номерки видає рецепція.',
      self: 'Номерків немає — клієнт сам бере вільну шафку.',
      none: 'Шафок немає.',
    },
    board: 'Номерки на дошці',
    boardHint: 'Діапазони й окремі номери через кому: «1-80, Ж1-Ж10, 81А».',
    boardCount: (n: number) => `На дошці ${keys(n)}`,
    boardInvalid: 'Не вдається прочитати номерки. Приклад: 1-80, Ж1-Ж10',
    boardState: (total: number, issued: number, lost: number) =>
      [keys(total), issued ? `видано ${issued}` : null, lost ? `загублено ${lost}` : null].filter(Boolean).join(' · '),
    autoClose: 'Візит закривається сам через',
    autoCloseUnit: 'хв',
    autoCloseHint: 'Якщо вихід ніхто не відмічає. Від 30 хвилин до 24 годин.',
    autoCloseInvalid: 'Від 30 до 1440 хвилин',
    saved: 'Налаштування збережено.',
    keysOut: (numbers: string) => `Ключі ще видано: ${numbers}. Поверніть їх або позначте загубленими — і тоді змініть.`,
    lockersRented: (numbers: string) => `Шафки в оренді: ${numbers}. Спершу завершіть оренду в «Шафках» — і тоді змініть.`,
    invalidBoard: 'Не вдається прочитати номерки. Приклад: 1-80, Ж1-Ж10',
    lostKeys: 'Загублені ключі',
    found: 'Знайшовся',
    readOnly: 'Змінювати налаштування входу може роль із правом «Налаштування: зміна».',
  },

  devices: {
    title: 'Пристрої',
    subtitle: 'Зчитувачі, термінали й турнікети біля дверей.',
    add: 'Додати пристрій',
    empty: 'Пристроїв ще немає',
    emptyMessage: 'Рецепція працює й без них. Пристрій потрібен для турнікета або зчитувача без людини.',
    columns: { name: 'Назва', kind: 'Тип', direction: 'Напрям', seen: 'На звʼязку', branch: 'Клуб' },
    kinds: { qr_reader: 'Зчитувач QR', card_reader: 'Зчитувач карток', face_terminal: 'Термінал обличчя', turnstile: 'Турнікет' },
    directions: { in: 'Вхід', out: 'Вихід', both: 'В обидва боки' },
    health: {
      never: 'Ще не виходив на звʼязок',
      ok: (ago: string) => `${ago} тому`,
      stale: (ago: string) => `Мовчить ${ago}`,
      off: 'Вимкнено',
    },
    rotate: 'Новий ключ',
    disable: 'Вимкнути',
    disableTitle: (name: string) => `Вимкнути «${name}»?`,
    disableMessage: 'Ключ пристрою перестане діяти одразу. Увімкнути знову можна новим ключем.',
    rotateTitle: (name: string) => `Новий ключ для «${name}»?`,
    rotateMessage: 'Старий ключ перестане діяти одразу — пристрій треба буде переналаштувати.',
    form: {
      title: 'Новий пристрій',
      name: 'Назва',
      namePlaceholder: 'Турнікет біля входу',
      nameRequired: 'Назвіть пристрій',
      kind: 'Тип',
      direction: 'Напрям',
      branch: 'Клуб',
      submit: 'Додати',
      submitting: 'Додаємо…',
    },
    keyTitle: 'Ключ пристрою',
    keyWarning: 'Скопіюйте ключ зараз — ми показуємо його лише раз і більше не зможемо.',
    keyId: 'Ідентифікатор',
    keyValue: 'Ключ',
    keyHeader: 'Заголовок для пристрою',
    keyDone: 'Я зберіг ключ',
  },

  methods: { desk: 'Рецепція', card: 'Картка', qr: 'QR', face: 'Обличчя' } as Readonly<Record<string, string>>,

  duration: {
    justNow: 'щойно',
    minutes: (m: number) => `${m} хв`,
    hours: (h: number) => `${h} год`,
    hoursMinutes: (h: number, m: number) => `${h} год ${m} хв`,
  },

  refusedGeneric: 'Вхід не дозволено.',
  refusals: {
    unknown_person: 'Не впізнали людину — знайдіть клієнта вручну.',
    no_pass: 'Немає абонемента, який пускає зараз.',
    already_inside: 'Клієнт уже в залі — вхід не закрито.',
    qr_expired: 'Код застарів — нехай оновить екран із кодом.',
    qr_invalid: 'Це не код eatlete або його підроблено.',
    qr_replayed: 'Цей код уже використали — потрібен новий.',
    face_not_enrolled: 'Обличчя не зареєстроване в цьому клубі.',
    device_unknown: 'Пристрій не зареєстровано або вимкнено.',
    card_unknown: 'Картку не привʼязано до жодного клієнта.',
    locker_taken: 'Цей ключ уже видано.',
    locker_unknown: 'Такого ключа немає на дошці.',
    locker_rented: 'Ця шафка в оренді в іншого клієнта.',
    branch_closed: 'Клуб зараз зачинено.',
    scan_on_entry: 'QR перевіряється одразу при вході.',
    pass_inactive: 'Абонемент недійсний.',
    zone_not_covered: 'Цей абонемент не відкриває цю зону.',
    weekday_not_allowed: 'Цей абонемент не діє в цей день тижня.',
    outside_day_window: 'Цей абонемент діє в інший час доби.',
    trainer_required: 'У цей час цей абонемент діє лише з тренером.',
    per_day_limit: 'Ліміт відвідувань на сьогодні вичерпано.',
    per_week_limit: 'Ліміт відвідувань на цей тиждень вичерпано.',
    per_month_limit: 'Ліміт відвідувань на цей місяць вичерпано.',
    no_visits_left: 'Відвідування закінчились.',
  } as Readonly<Record<string, string>>,

  /// «Вхід → Шафки» — locker rental (specs/access.md §10b) and the key board.
  lockers: {
    subtitle: 'Оренда шаф і дошка ключів — у кого яка шафка зараз.',
    rent: 'Здати в оренду',
    figures: { free: 'Вільні', issued: 'Видано', rented: 'В оренді' },
    rentals: 'Оренда',
    filter: 'Показати',
    /// «Закінчуються» is short so the three fit a phone; the 7 days are said
    /// under the list (`endingHint`).
    filters: { active: 'Діють', ending: 'Закінчуються', ended: 'Завершені' },
    endingHint: 'Закінчуються за 7 днів — останній день сьогодні або протягом тижня. Нагадайте клієнту продовжити.',
    columns: { locker: 'Шафка', client: 'Клієнт', period: 'Період', left: 'Лишилось', state: 'Стан', price: 'Ціна' },
    number: (number: string) => `№${number}`,
    period: (from: string, to: string) => `${from} – ${to}`,
    left: (n: number) => (n === 0 ? 'останній день' : `ще ${days(n)}`),
    startsOn: (date: string) => `з ${date}`,
    endedOn: (date: string) => `завершено ${date}`,
    cancelled: 'скасовано',
    over: 'закінчилась',
    renewed: 'продовжено',
    until: (date: string) => `до ${date}`,
    free: 'без оплати',
    emptyActive: 'Жодна шафка не в оренді',
    emptyActiveMessage: 'Здайте шафку клієнту на місяць чи рік — і нікому іншому її не видадуть.',
    emptyEnding: 'Цього тижня оренди не закінчуються',
    emptyEnded: 'Завершених оренд ще немає',
    noLockers: 'У цьому клубі немає шафок',
    noLockersMessage: 'Увімкніть шафки в «Налаштуваннях» — тоді їх можна здавати в оренду.',
    openSettings: 'До налаштувань',
    board: 'Дошка ключів',
    boardHint: 'Натисніть вільну шафку, щоб здати її в оренду, або орендовану — щоб продовжити чи завершити.',
    boardEmpty: 'На дошці ще немає номерків — додайте їх у «Налаштуваннях».',
    selfHint: 'Номерків у клубі немає: оренда закріплює за клієнтом номер шафки, на якій його замок.',
    tile: {
      free: 'Вільна',
      issued: (name: string) => `Видано: ${name}`,
      rented: (name: string, date: string) => `Оренда: ${name} до ${date}`,
      keyWithRenter: 'ключ у клієнта',
      lost: 'Ключ загублено',
      out_of_service: 'Не працює',
      someone: 'клієнт',
    },
    form: {
      title: 'Здати шафку в оренду',
      client: 'Клієнт',
      clientRequired: 'Оберіть клієнта',
      locker: 'Шафка',
      lockerPlaceholder: 'Оберіть шафку',
      lockerHint: (n: number) => `Вільних на ці дні: ${n}`,
      noFree: 'На ці дні вільних шафок немає — змініть дати.',
      lockerRequired: 'Оберіть шафку',
      label: 'Номер шафки',
      labelHint: 'Номер, який закріплюєте за клієнтом.',
      labelRequired: 'Вкажіть номер шафки',
      startsOn: 'З',
      length: 'На скільки',
      presets: { '1': '1 міс.', '3': '3 міс.', '6': '6 міс.', '12': '12 міс.', dates: 'Дати' } as Readonly<Record<string, string>>,
      endsOn: 'По',
      until: (date: string, length: string) => `До ${date} включно · ${length}`,
      days,
      problems: {
        empty: 'Вкажіть, по який день.',
        backwards: 'Кінець раніше за початок.',
        too_long: 'Не довше трьох років.',
        over: 'Ця оренда вже минула.',
      } as Readonly<Record<string, string>>,
      price: 'Ціна',
      priceUnit: '₴',
      priceHint: 'Клієнт платить на рецепції — тут ціна лише записується.',
      priceInvalid: 'Сума в гривнях, наприклад 300',
      note: 'Примітка',
      notePlaceholder: 'Наприклад: ключ у клієнта',
      submit: 'Здати в оренду',
      submitting: 'Здаємо…',
      done: (number: string, name: string) => `Шафку №${number} здано в оренду: ${name}.`,
    },
    renew: {
      title: 'Продовжити оренду',
      now: (until: string) => `Зараз — до ${until}`,
      over: (lastDay: string) => `Закінчилась ${lastDay}`,
      from: (date: string) => `Нова оренда — з ${date}`,
      submit: 'Продовжити',
      submitting: 'Продовжуємо…',
      done: (number: string, until: string) => `Оренду шафки №${number} продовжено до ${until}.`,
    },
    details: {
      title: (number: string) => `Шафка №${number}`,
      client: 'Клієнт',
      period: 'Період',
      price: 'Ціна',
      note: 'Примітка',
      created: 'Здано',
      ended: 'Завершено',
      card: 'Картка клієнта',
      renew: 'Продовжити',
      end: 'Завершити',
      cancel: 'Скасувати',
      priceFootnote: 'Оплата — на рецепції; каса поки не приймає гроші за шафки.',
    },
    end: {
      title: (number: string) => `Завершити оренду шафки №${number}?`,
      cancelTitle: (number: string) => `Скасувати оренду шафки №${number}?`,
      message: 'Шафка звільниться сьогодні — її зможе взяти будь-хто.',
      messageKeys: 'Шафка звільниться сьогодні — заберіть у клієнта ключ.',
      messageUpcoming: 'Оренда ще не почалась — її буде скасовано.',
      confirm: 'Завершити',
      confirmCancel: 'Скасувати оренду',
      done: (number: string) => `Оренду шафки №${number} завершено — шафка вільна.`,
      cancelled: (number: string) => `Оренду шафки №${number} скасовано.`,
    },
    errors: {
      rented: 'Ця шафка вже в оренді на ці дні.',
      rentedUntil: (date: string) => `Ця шафка вже в оренді на ці дні — до ${date}.`,
      inUse: 'Ключ від цієї шафки зараз у іншого клієнта — дочекайтесь, поки його повернуть.',
      unavailable: 'Ключ від цієї шафки загублено або шафка не працює.',
      unknown: 'Такої шафки немає на дошці.',
      noLockers: 'У цьому клубі немає шафок.',
      invalidPeriod: 'Перевірте дати: кінець не раніше початку, не довше трьох років.',
      periodInPast: 'Ця оренда вже минула.',
      unknownClient: 'Такого клієнта немає — оновіть сторінку.',
      clientInactive: 'Клієнт в архіві — спершу відновіть його картку.',
      branchArchived: 'Цей клуб закрито.',
      over: 'Ця оренда вже завершилась.',
      unknownRental: 'Такої оренди немає — оновіть сторінку.',
    },
  },

  /// The line on the client card (specs/access.md §10b).
  clientLocker: {
    title: 'Шафка',
    line: (number: string, until: string) => `Шафка №${number} в оренді до ${until}`,
    upcoming: (number: string, from: string, until: string) => `Шафка №${number} в оренді з ${from} до ${until}`,
    left: (n: number) => (n === 0 ? 'Сьогодні останній день' : `Лишилось ${days(n)}`),
  },

  errors: {
    lockerTaken: 'Цей ключ уже видано іншому клієнту.',
    lockerUnknown: 'Такого ключа немає на дошці.',
    lockerRented: 'Ця шафка в оренді в іншого клієнта.',
    noKey: 'На цьому візиті немає ключа.',
    notKeysMode: 'У цьому клубі номерків не видають.',
    visitOver: 'Цей візит уже закінчився.',
    unknownVisit: 'Такого візиту немає — оновіть сторінку.',
    unknownDevice: 'Такого пристрою немає — оновіть сторінку.',
  },
}
